import { ClipboardList, Edit3, Plus, Trash2, UserPlus } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import { ApiError, apiRequest } from "../api";
import { useAuth } from "../auth";
import { ErrorState, LoadingState } from "../components/PageState";
import { matchesQuickSearch, QuickSearch } from "../components/QuickSearch";
import { SearchSelect } from "../components/SearchSelect";
import { Message, Modal, PageHeader, Tabs } from "../components/Ui";
import type {
  ContactRow,
  FoundationData,
  ItemListRow,
  ItemType,
  PartnerRow,
  PartnerType,
  UomRow
} from "../types";

type Tab = PartnerType | ItemType | "Uom";

const tabs = [
  { value: "Customer", label: "Khách hàng" },
  { value: "Supplier", label: "Nhà cung cấp" },
  { value: "ExternalService", label: "Dịch vụ ngoài" },
  { value: "GovernmentAgency", label: "Cơ quan nhà nước" },
  { value: "Product", label: "Sản phẩm" },
  { value: "Material", label: "Nguyên liệu" },
  { value: "Packaging", label: "Bao bì" },
  { value: "OtherSupply", label: "Vật tư khác" },
  { value: "Uom", label: "Đơn vị tính" }
];

const partnerTypes = new Set(["Customer", "Supplier", "ExternalService", "GovernmentAgency"]);
const itemTypes = new Set(["Product", "Material", "Packaging", "OtherSupply"]);
const blankContact = (): ContactRow => ({ name: "", phone: "", email: "", position: "" });
const blankCustomerReference = () => ({ customerId: "", customerItemCode: "", customerItemName: "", customerVariantName: "" });

const blankPartner = (partnerType: PartnerType) => ({
  code: "",
  name: "",
  partnerType,
  taxCode: "",
  address: "",
  phone: "",
  email: "",
  isActive: true,
  contacts: [blankContact()]
});

const blankItem = (itemType: ItemType, uomId = "") => ({
  code: "",
  name: "",
  itemType,
  baseUomId: uomId,
  specificationSymbol: "",
  isLotTracked: itemType === "Product",
  trackMinimumStock: false,
  minimumStockQuantity: null as number | null,
  isActive: true,
  packagingProfile: "",
  shelfLifeDays: null as number | null,
  piecesPerBag: null as number | null,
  bagsPerBox: null as number | null,
  boxesPerCarton: null as number | null,
  customerReferences: [] as Array<ReturnType<typeof blankCustomerReference>>
});

export function MasterDataPage() {
  const { user } = useAuth();
  const isAdmin = user?.roles.includes("ADMIN") === true;
  const hasPermission = (permission: string) => isAdmin || user?.permissions.includes(permission) === true;
  const [tab, setTab] = useState<Tab>("Customer");
  const [partners, setPartners] = useState<PartnerRow[]>([]);
  const [items, setItems] = useState<ItemListRow[]>([]);
  const [uoms, setUoms] = useState<UomRow[]>([]);
  const [foundation, setFoundation] = useState<FoundationData | null>(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [partnerForm, setPartnerForm] = useState<ReturnType<typeof blankPartner> | null>(null);
  const [editingPartnerId, setEditingPartnerId] = useState<string | null>(null);
  const [itemForm, setItemForm] = useState<ReturnType<typeof blankItem> | null>(null);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [uomForm, setUomForm] = useState({ code: "", name: "", symbol: "", decimalPlaces: 0, isActive: true });
  const [editingUomId, setEditingUomId] = useState<string | null>(null);
  const [showUomForm, setShowUomForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [partnerData, itemData, uomData, foundationData] = await Promise.all([
        apiRequest<PartnerRow[]>("/api/master-data/partners"),
        apiRequest<ItemListRow[]>("/api/items"),
        apiRequest<UomRow[]>("/api/master-data/uoms"),
        apiRequest<FoundationData>("/api/master-data/foundation")
      ]);
      setPartners(partnerData);
      setItems(itemData);
      setUoms(uomData);
      setFoundation(foundationData);
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Không tải được dữ liệu danh mục.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filteredPartners = useMemo(() => partners.filter((row) =>
    row.partnerType === tab && matchesQuickSearch(search, row.code, row.name, row.taxCode, row.phone, row.email, row.address,
      row.contacts.map((contact) => `${contact.name} ${contact.phone ?? ""} ${contact.email ?? ""}`).join(" "))
  ), [partners, tab, search]);
  const filteredItems = useMemo(() => items.filter((row) =>
    row.itemType === tab && matchesQuickSearch(search, row.code, row.name, row.category, row.baseUom, row.specificationSymbol,
      row.customerReferences?.map((reference) => `${reference.customerName ?? ""} ${reference.customerItemCode} ${reference.customerItemName}`).join(" "))
  ), [items, tab, search]);
  const filteredUoms = useMemo(() => uoms.filter((row) =>
    matchesQuickSearch(search, row.code, row.name, row.symbol, row.decimalPlaces)
  ), [uoms, search]);
  const uomSearchOptions = useMemo(() => uoms.filter((x) => x.isActive).map((x) => ({
    value: x.id, label: `${x.code} – ${x.name}`, selectedLabel: x.name, keywords: `${x.code} ${x.name} ${x.symbol}`
  })), [uoms]);
  const customerSearchOptions = useMemo(() => partners
    .filter((partner) => partner.partnerType === "Customer" && partner.isActive)
    .map((partner) => ({
      value: partner.id,
      label: `${partner.code} – ${partner.name}`,
      keywords: `${partner.code} ${partner.name} ${partner.taxCode ?? ""}`
    })), [partners]);

  const openCreate = () => {
    setMessage("");
    if (partnerTypes.has(tab)) {
      setEditingPartnerId(null);
      setPartnerForm(blankPartner(tab as PartnerType));
    } else if (itemTypes.has(tab)) {
      setEditingItemId(null);
      setItemForm(blankItem(tab as ItemType, foundation?.uoms[0]?.id));
    } else {
      setEditingUomId(null);
      setUomForm({ code: "", name: "", symbol: "", decimalPlaces: 0, isActive: true });
      setShowUomForm(true);
    }
  };

  const editPartner = (row: PartnerRow) => {
    setEditingPartnerId(row.id);
    setPartnerForm({
      code: row.code,
      name: row.name,
      partnerType: row.partnerType,
      taxCode: row.taxCode ?? "",
      address: row.address ?? "",
      phone: row.phone ?? "",
      email: row.email ?? "",
      isActive: row.isActive,
      contacts: row.contacts.length ? row.contacts.map((x) => ({ ...x })) : [blankContact()]
    });
  };

  const savePartner = async () => {
    if (!partnerForm) return;
    setError("");
    try {
      await apiRequest(
        editingPartnerId ? `/api/master-data/partners/${editingPartnerId}` : "/api/master-data/partners",
        { method: editingPartnerId ? "PUT" : "POST", body: JSON.stringify(partnerForm) }
      );
      setPartnerForm(null);
      setMessage("Đã lưu thông tin đơn vị và người liên hệ.");
      await load();
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Không lưu được thông tin đơn vị.");
    }
  };

  const editItem = (row: ItemListRow) => {
    setEditingItemId(row.id);
    setItemForm({
      code: row.code,
      name: row.name,
      itemType: row.itemType,
      baseUomId: row.baseUomId,
      specificationSymbol: row.specificationSymbol ?? "",
      isLotTracked: row.isLotTracked,
      trackMinimumStock: row.trackMinimumStock,
      minimumStockQuantity: row.minimumStockQuantity ?? null,
      isActive: row.isActive,
      packagingProfile: "",
      shelfLifeDays: null,
      piecesPerBag: row.packagingConversion?.piecesPerBag ?? null,
      bagsPerBox: row.packagingConversion?.bagsPerBox ?? null,
      boxesPerCarton: row.packagingConversion?.boxesPerCarton ?? null,
      customerReferences: (row.customerReferences ?? []).map((reference) => ({
        customerId: reference.customerId,
        customerItemCode: reference.customerItemCode,
        customerItemName: reference.customerItemName,
        customerVariantName: reference.customerVariantName ?? row.name
      }))
    });
  };

  const saveItem = async () => {
    if (!itemForm) return;
    setError("");
    try {
      await apiRequest(editingItemId ? `/api/items/${editingItemId}` : "/api/items", {
        method: editingItemId ? "PUT" : "POST",
        body: JSON.stringify(itemForm)
      });
      setItemForm(null);
      setMessage("Đã lưu mã hàng và đơn vị tính.");
      await load();
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Không lưu được mã hàng.");
    }
  };

  const editUom = (row: UomRow) => {
    setEditingUomId(row.id);
    setUomForm({
      code: row.code,
      name: row.name,
      symbol: row.symbol,
      decimalPlaces: row.decimalPlaces ?? 0,
      isActive: row.isActive ?? true
    });
    setShowUomForm(true);
  };

  const saveUom = async () => {
    setError("");
    try {
      await apiRequest(editingUomId ? `/api/master-data/uoms/${editingUomId}` : "/api/master-data/uoms", {
        method: editingUomId ? "PUT" : "POST",
        body: JSON.stringify(uomForm)
      });
      setShowUomForm(false);
      setMessage("Đã lưu đơn vị tính.");
      await load();
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Không lưu được đơn vị tính.");
    }
  };

  if (loading && !foundation) return <LoadingState />;
  if (error && !foundation) return <ErrorState message={error} retry={load} />;

  return (
    <div className="module-page">
      <PageHeader
        eyebrow="DỮ LIỆU DÙNG CHUNG"
        title="Danh mục"
        description="Tự thêm khách hàng, nhà cung cấp, đơn vị liên quan, sản phẩm, nguyên liệu và đơn vị tính."
        action={hasPermission("MasterData.Create") ? <button className="primary-button" onClick={openCreate}><Plus size={17} /> Thêm mới</button> : undefined}
      />
      <Tabs tabs={tabs} value={tab} onChange={(value) => { setTab(value as Tab); setSearch(""); }} />
      {message && <Message tone="success">{message}</Message>}
      {error && <Message tone="error">{error}</Message>}
      <section className="panel module-panel">
        <QuickSearch
          value={search}
          onChange={setSearch}
          placeholder={partnerTypes.has(tab) ? "Tìm mã, tên, MST, điện thoại, email hoặc người liên hệ..." : tab === "Product" ? "Tìm mã/tên nội bộ hoặc mã/tên hàng theo khách hàng..." : itemTypes.has(tab) ? "Tìm mã, tên, nhóm, đơn vị hoặc quy cách..." : "Tìm mã, tên hoặc ký hiệu đơn vị..."}
          resultCount={partnerTypes.has(tab) ? filteredPartners.length : itemTypes.has(tab) ? filteredItems.length : filteredUoms.length}
        />

        {partnerTypes.has(tab) && (
          <div className="data-table-wrapper">
            <table className="data-table">
              <thead><tr><th className="stt-column">STT</th><th>Mã</th><th>Tên đơn vị</th><th>Thông tin liên hệ</th><th>Người liên hệ</th><th>Trạng thái</th><th /></tr></thead>
              <tbody>
                {filteredPartners.map((row, index) => (
                  <tr key={row.id}>
                    <td className="stt-column">{index + 1}</td>
                    <td><strong>{row.code}</strong></td>
                    <td><strong>{row.name}</strong><small className="table-subtext">{row.address || "Chưa có địa chỉ"}</small></td>
                    <td>{row.phone || "—"}<small className="table-subtext">{row.email || "—"}</small></td>
                    <td>{row.contacts.length ? row.contacts.map((x) => x.name).join(", ") : "Chưa khai báo"}</td>
                    <td><span className={`status-pill ${row.isActive ? "success" : "neutral"}`}>{row.isActive ? "Đang dùng" : "Ngừng dùng"}</span></td>
                    <td>{hasPermission("MasterData.Edit") && <button className="table-action" onClick={() => editPartner(row)}><Edit3 size={15} /> Sửa</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {itemTypes.has(tab) && (
          <div className="data-table-wrapper">
            <table className="data-table">
              <thead><tr><th className="stt-column">STT</th><th>Mã</th><th>Tên</th><th>Đơn vị tính</th><th>{tab === "Product" ? "Mã/Tên theo khách hàng" : "Quy cách"}</th><th>Theo lô</th><th>Tồn tối thiểu</th><th /></tr></thead>
              <tbody>
                {filteredItems.map((row, index) => (
                  <tr key={row.id}>
                    <td className="stt-column">{index + 1}</td>
                    <td><strong>{row.code}</strong></td>
                    <td>{row.name}</td><td>{row.baseUom}</td><td>{row.itemType === "Product"
                      ? row.customerReferences?.length
                        ? row.customerReferences.map((reference) => <span className="stacked-line customer-item-reference-summary" key={`${row.id}-${reference.customerId}`}><strong>{reference.customerCode}</strong>: {reference.customerItemCode}<small>{reference.customerItemName}</small></span>)
                        : <span className="table-subtext">Dùng mã/tên nội bộ</span>
                      : row.specificationSymbol || "—"}</td>
                    <td>{row.isLotTracked ? "Có" : "Không"}</td>
                    <td>{row.trackMinimumStock ? <strong>{row.minimumStockQuantity} {row.baseUom}</strong> : "Không theo dõi"}</td>
                    <td><div className="table-actions master-data-actions">
                      {row.itemType === "Product" && <Link className="table-action" href={`/danh-muc/san-pham/${encodeURIComponent(row.code)}`}><ClipboardList size={15} /> BOM</Link>}
                      {hasPermission("MasterData.Edit") && <button className="table-action" onClick={() => editItem(row)}><Edit3 size={15} /> Sửa</button>}
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "Uom" && (
          <div className="data-table-wrapper">
            <table className="data-table">
              <thead><tr><th className="stt-column">STT</th><th>Mã</th><th>Tên đơn vị</th><th>Ký hiệu</th><th>Số lẻ</th><th>Trạng thái</th><th /></tr></thead>
              <tbody>
                {filteredUoms.map((row, index) => (
                  <tr key={row.id}>
                    <td className="stt-column">{index + 1}</td>
                    <td><strong>{row.code}</strong></td><td>{row.name}</td><td>{row.symbol}</td><td>{row.decimalPlaces ?? 0}</td>
                    <td>{row.isActive === false ? "Ngừng dùng" : "Đang dùng"}</td>
                    <td>{hasPermission("MasterData.Edit") && <button className="table-action" onClick={() => editUom(row)}><Edit3 size={15} /> Sửa</button>}</td>
                  </tr>
                ))}
                {!filteredUoms.length && <tr><td colSpan={7}><div className="empty-state">Không có đơn vị tính phù hợp bộ lọc.</div></td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {partnerForm && (
        <Modal title={editingPartnerId ? "Sửa thông tin đơn vị" : "Thêm đơn vị mới"} onClose={() => setPartnerForm(null)} wide error={error}>
          <div className="form-grid two">
            <label>Mã đơn vị *<input value={partnerForm.code} disabled={!!editingPartnerId} onChange={(e) => setPartnerForm({ ...partnerForm, code: e.target.value })} /></label>
            <label>Tên đơn vị *<input value={partnerForm.name} onChange={(e) => setPartnerForm({ ...partnerForm, name: e.target.value })} /></label>
            <label>Mã số thuế<input value={partnerForm.taxCode} onChange={(e) => setPartnerForm({ ...partnerForm, taxCode: e.target.value })} /></label>
            <label>Số điện thoại<input value={partnerForm.phone} onChange={(e) => setPartnerForm({ ...partnerForm, phone: e.target.value })} /></label>
            <label className="span-two">Địa chỉ<textarea value={partnerForm.address} onChange={(e) => setPartnerForm({ ...partnerForm, address: e.target.value })} /></label>
            <label>Email<input type="email" value={partnerForm.email} onChange={(e) => setPartnerForm({ ...partnerForm, email: e.target.value })} /></label>
            <label className="checkbox-label"><input type="checkbox" checked={partnerForm.isActive} onChange={(e) => setPartnerForm({ ...partnerForm, isActive: e.target.checked })} /> Đang sử dụng</label>
          </div>
          <div className="subform-heading"><div><UserPlus size={18} /><strong>Người liên hệ</strong></div></div>
          <div className="contact-list">
            {partnerForm.contacts.map((contact, index) => (
              <div className="contact-card" key={index}>
                <span className="line-number">{index + 1}</span>
                <input placeholder="Họ và tên *" value={contact.name} onChange={(e) => {
                  const contacts = [...partnerForm.contacts]; contacts[index] = { ...contact, name: e.target.value }; setPartnerForm({ ...partnerForm, contacts });
                }} />
                <input placeholder="Số điện thoại" value={contact.phone ?? ""} onChange={(e) => {
                  const contacts = [...partnerForm.contacts]; contacts[index] = { ...contact, phone: e.target.value }; setPartnerForm({ ...partnerForm, contacts });
                }} />
                <input placeholder="Email" value={contact.email ?? ""} onChange={(e) => {
                  const contacts = [...partnerForm.contacts]; contacts[index] = { ...contact, email: e.target.value }; setPartnerForm({ ...partnerForm, contacts });
                }} />
                <button className="remove-button" onClick={() => setPartnerForm({ ...partnerForm, contacts: partnerForm.contacts.filter((_, i) => i !== index) })}>Xóa</button>
              </div>
            ))}
          </div>
          <div className="add-line-below"><button className="secondary-button" onClick={() => setPartnerForm({ ...partnerForm, contacts: [...partnerForm.contacts, blankContact()] })}><Plus size={15} /> Thêm người liên hệ</button></div>
          <div className="form-actions"><button className="secondary-button" onClick={() => setPartnerForm(null)}>Đóng</button><button className="primary-button" onClick={() => void savePartner()}>Lưu danh mục</button></div>
        </Modal>
      )}

      {itemForm && (
        <Modal title={editingItemId ? "Sửa mã hàng" : "Thêm mã hàng mới"} onClose={() => setItemForm(null)} wide error={error}>
          <div className="form-grid">
            <label>Mã *<input value={itemForm.code} onChange={(e) => setItemForm({ ...itemForm, code: e.target.value })} /></label>
            <label>Tên *<input value={itemForm.name} onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })} /></label>
            <label>Nhóm hàng
              <select value={itemForm.itemType} onChange={(e) => {
                const itemType = e.target.value as ItemType;
                setItemForm({
                  ...itemForm,
                  itemType,
                  isLotTracked: itemType === "Product",
                  specificationSymbol: itemType === "Product" ? "" : itemForm.specificationSymbol,
                  customerReferences: itemType === "Product" ? itemForm.customerReferences : []
                });
              }}>
                <option value="Product">Sản phẩm</option><option value="Material">Nguyên liệu</option>
                <option value="Packaging">Bao bì</option><option value="OtherSupply">Vật tư khác</option>
              </select>
            </label>
            <label>Đơn vị tính *
              <SearchSelect value={itemForm.baseUomId} options={uomSearchOptions} onChange={(baseUomId) => setItemForm({ ...itemForm, baseUomId })} placeholder="Nhập mã hoặc tên đơn vị" />
            </label>
            {itemForm.itemType !== "Product" && <label>Quy cách / ký hiệu<input value={itemForm.specificationSymbol} onChange={(e) => setItemForm({ ...itemForm, specificationSymbol: e.target.value })} /></label>}
            <label className="checkbox-label"><input type="checkbox" checked={itemForm.itemType === "Product" || itemForm.isLotTracked} disabled={itemForm.itemType === "Product"} onChange={(e) => setItemForm({ ...itemForm, isLotTracked: e.target.checked })} /> {itemForm.itemType === "Product" ? "Thành phẩm luôn theo dõi số lô" : "Theo dõi theo số lô"}</label>
            <label className="checkbox-label"><input type="checkbox" checked={itemForm.trackMinimumStock} onChange={(e) => setItemForm({ ...itemForm, trackMinimumStock: e.target.checked })} /> Theo dõi mức tồn tối thiểu</label>
            <label>Mức tồn tối thiểu ({uoms.find((uom) => uom.id === itemForm.baseUomId)?.name ?? "đơn vị chính"})
              <input type="number" min="0.000001" step="any" disabled={!itemForm.trackMinimumStock} required={itemForm.trackMinimumStock} placeholder={itemForm.trackMinimumStock ? "Nhập số lượng lớn hơn 0" : "Bật theo dõi để nhập"} value={itemForm.minimumStockQuantity ?? ""} onChange={(e) => setItemForm({ ...itemForm, minimumStockQuantity: e.target.value === "" ? null : Number(e.target.value) })} />
            </label>
          </div>
          {itemForm.itemType === "Product" && <>
            <div className="subform-heading"><div><strong>Mã và tên sản phẩm theo từng khách hàng</strong></div></div>
            <div className="customer-item-reference-editor">
              <div className="customer-item-reference-head customer-reference-five-fields"><span>STT</span><span>Khách hàng</span><span>Mã hàng KH</span><span>Tên hàng chung trên PO</span><span>Tên mẫu/màu khi giao</span><span /></div>
              {itemForm.customerReferences.map((reference, index) => <div className="customer-item-reference-row" key={`${reference.customerId}-${index}`}>
                <span className="line-number">{index + 1}</span>
                <SearchSelect value={reference.customerId} options={customerSearchOptions.filter((option) => option.value === reference.customerId || !itemForm.customerReferences.some((row, rowIndex) => rowIndex !== index && row.customerId === option.value))} onChange={(customerId) => { const customerReferences = [...itemForm.customerReferences]; customerReferences[index] = { ...reference, customerId }; setItemForm({ ...itemForm, customerReferences }); }} placeholder="Chọn khách hàng" />
                <input value={reference.customerItemCode} onChange={(event) => { const customerReferences = [...itemForm.customerReferences]; customerReferences[index] = { ...reference, customerItemCode: event.target.value }; setItemForm({ ...itemForm, customerReferences }); }} placeholder="Mã trên PO" />
                <input value={reference.customerItemName} onChange={(event) => { const customerReferences = [...itemForm.customerReferences]; customerReferences[index] = { ...reference, customerItemName: event.target.value }; setItemForm({ ...itemForm, customerReferences }); }} placeholder="Tên trên PO/chứng từ KH" />
                <input value={reference.customerVariantName} onChange={(event) => { const customerReferences = [...itemForm.customerReferences]; customerReferences[index] = { ...reference, customerVariantName: event.target.value }; setItemForm({ ...itemForm, customerReferences }); }} placeholder="Doremon, Kitty, Hồng..." />
                <button className="remove-icon" title="Xóa liên kết" onClick={() => setItemForm({ ...itemForm, customerReferences: itemForm.customerReferences.filter((_, rowIndex) => rowIndex !== index) })}><Trash2 size={16} /></button>
              </div>)}
              {!itemForm.customerReferences.length && <div className="empty-inline">Chưa khai báo: đơn hàng và chứng từ sẽ tự dùng mã, tên nội bộ.</div>}
              <div className="add-line-below"><button className="secondary-button" onClick={() => setItemForm({ ...itemForm, customerReferences: [...itemForm.customerReferences, blankCustomerReference()] })}><Plus size={15} /> Thêm mã theo khách hàng</button></div>
            </div>
            <div className="subform-heading"><div><strong>Công thức quy đổi đóng gói của sản phẩm</strong></div></div>
            <div className="form-grid three">
              <label>Số Cái trong 1 Bịch<input type="number" min="0" step="any" placeholder="Ví dụ: 10" value={itemForm.piecesPerBag ?? ""} onChange={(e) => setItemForm({ ...itemForm, piecesPerBag: e.target.value === "" ? null : Number(e.target.value) })} /></label>
              <label>Số Bịch trong 1 Hộp<input type="number" min="0" step="any" placeholder="Ví dụ: 10" value={itemForm.bagsPerBox ?? ""} onChange={(e) => setItemForm({ ...itemForm, bagsPerBox: e.target.value === "" ? null : Number(e.target.value) })} /></label>
              <label>Số Hộp trong 1 Thùng<input type="number" min="0" step="any" placeholder="Ví dụ: 100" value={itemForm.boxesPerCarton ?? ""} onChange={(e) => setItemForm({ ...itemForm, boxesPerCarton: e.target.value === "" ? null : Number(e.target.value) })} /></label>
            </div>
            <small className="table-subtext">ERP tự suy ra quy đổi Hộp/Thùng sang Cái. Kho, sản xuất và BOM luôn dùng mã/tên nội bộ.</small>
          </>}
          <div className="form-actions"><button className="secondary-button" onClick={() => setItemForm(null)}>Đóng</button><button className="primary-button" onClick={() => void saveItem()}>Lưu mã hàng</button></div>
        </Modal>
      )}

      {showUomForm && (
        <Modal title={editingUomId ? "Sửa đơn vị tính" : "Thêm đơn vị tính"} onClose={() => setShowUomForm(false)} error={error}>
          <div className="form-grid">
            <label>Mã *<input disabled={!!editingUomId} value={uomForm.code} onChange={(e) => setUomForm({ ...uomForm, code: e.target.value })} /></label>
            <label>Tên *<input value={uomForm.name} onChange={(e) => setUomForm({ ...uomForm, name: e.target.value })} /></label>
            <label>Ký hiệu<input value={uomForm.symbol} onChange={(e) => setUomForm({ ...uomForm, symbol: e.target.value })} /></label>
            <label>Số chữ số thập phân<input type="number" min="0" max="6" value={uomForm.decimalPlaces} onChange={(e) => setUomForm({ ...uomForm, decimalPlaces: Number(e.target.value) })} /></label>
          </div>
          <div className="form-actions"><button className="secondary-button" onClick={() => setShowUomForm(false)}>Đóng</button><button className="primary-button" onClick={() => void saveUom()}>Lưu đơn vị</button></div>
        </Modal>
      )}
    </div>
  );
}
